var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { spriteHref, spritePaths } from "~/utils/sprite";
export const ROTATING_BRAND_LINE_TAG = "rotating-brand-line";
const BRAND_LOGOS = [
    {
        name: "State Farm",
        symbolId: "brand-statefarm-color",
        width: 140,
        height: 19,
        maxWidth: 134,
        maxHeight: 25,
    },
    {
        name: "Allstate",
        symbolId: "brand-allstate-color",
        width: 140,
        height: 30,
        maxWidth: 127,
        maxHeight: 27,
    },
    {
        name: "GEICO",
        symbolId: "geico-color",
        width: 376,
        height: 65,
        maxWidth: 136,
        maxHeight: 24,
    },
    {
        name: "Travelers",
        symbolId: "travelers-color",
        width: 516,
        height: 105,
        maxWidth: 136,
        maxHeight: 27,
    },
    {
        name: "Farmers",
        symbolId: "brand-farmers-color",
        width: 140,
        height: 27,
        maxWidth: 127,
        maxHeight: 25,
    },
    {
        name: "Progressive",
        symbolId: "brand-progressive-color",
        width: 140,
        height: 17,
        maxWidth: 134,
        maxHeight: 20,
    },
    {
        name: "Direct Auto",
        symbolId: "direct-auto-color",
        width: 106,
        height: 41,
        maxWidth: 82,
        maxHeight: 32,
    },
    {
        name: "Chubb",
        symbolId: "chubb-color",
        width: 971,
        height: 99,
        maxWidth: 140,
        maxHeight: 18,
    },
    {
        name: "Liberty Mutual",
        symbolId: "brand-liberty-mutual-color",
        width: 140,
        height: 36,
        maxWidth: 122,
        maxHeight: 32,
    },
    {
        name: "USAA",
        symbolId: "brand-usaa-color",
        width: 117,
        height: 31,
        maxWidth: 104,
        maxHeight: 27,
    },
    {
        name: "Nationwide",
        symbolId: "nationwide-color",
        width: 430,
        height: 126,
        maxWidth: 124,
        maxHeight: 36,
    },
    {
        name: "The General",
        symbolId: "the-general",
        width: 254,
        height: 63,
        maxWidth: 118,
        maxHeight: 29,
    },
];
const DESKTOP_SLOT_COUNT = 5;
const ROTATION_STEP = DESKTOP_SLOT_COUNT;
const ROTATION_MS = 3200;
const STAGGER_MS = 120;
const TRANSITION_MS = 520;
const ENTER_DELAY_MS = 160;
let RotatingBrandLineElement = class RotatingBrandLineElement extends LitElement {
    constructor() {
        super(...arguments);
        this.label = "Discover quotes from over 80 providers";
        this.activeLogoOffset = 0;
    }
    connectedCallback() {
        super.connectedCallback();
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }
        this.rotationIntervalId = window.setInterval(() => this.rotateLogos(), ROTATION_MS);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.clearTimers();
    }
    clearTimers() {
        if (this.rotationIntervalId) {
            window.clearInterval(this.rotationIntervalId);
            this.rotationIntervalId = undefined;
        }
        if (this.transitionTimeoutId) {
            window.clearTimeout(this.transitionTimeoutId);
            this.transitionTimeoutId = undefined;
        }
    }
    rotateLogos() {
        this.previousLogoOffset = this.activeLogoOffset;
        this.activeLogoOffset =
            (this.activeLogoOffset + ROTATION_STEP) % BRAND_LOGOS.length;
        if (this.transitionTimeoutId) {
            window.clearTimeout(this.transitionTimeoutId);
        }
        this.transitionTimeoutId = window.setTimeout(() => {
            this.previousLogoOffset = undefined;
            this.transitionTimeoutId = undefined;
        }, TRANSITION_MS + ENTER_DELAY_MS + (DESKTOP_SLOT_COUNT - 1) * STAGGER_MS);
    }
    getSlotLogoIndex(slotIndex, logoOffset) {
        return (logoOffset + slotIndex) % BRAND_LOGOS.length;
    }
    getLogoState(logoIndex, slotIndex) {
        const activeLogoIndex = this.getSlotLogoIndex(slotIndex, this.activeLogoOffset);
        if (logoIndex === activeLogoIndex)
            return "current";
        if (this.previousLogoOffset === undefined)
            return "hidden";
        const previousLogoIndex = this.getSlotLogoIndex(slotIndex, this.previousLogoOffset);
        return logoIndex === previousLogoIndex ? "outgoing" : "hidden";
    }
    renderLogoLayer(logo, logoIndex, slotIndex) {
        const href = spriteHref(spritePaths.brands, logo.symbolId);
        const logoState = this.getLogoState(logoIndex, slotIndex);
        const transitionDelay = slotIndex * STAGGER_MS +
            (logoState === "current" && this.previousLogoOffset !== undefined
                ? ENTER_DELAY_MS
                : 0);
        return html `
            <svg
                aria-hidden="true"
                focusable="false"
                class="logo-layer"
                data-logo-state=${logoState}
                style="--rotating-brand-logo-delay: ${transitionDelay}ms; --rotating-brand-logo-max-inline-size: ${logo.maxWidth}px; --rotating-brand-logo-max-block-size: ${logo.maxHeight}px"
                width=${logo.width}
                height=${logo.height}
            >
                <use href=${href}></use>
            </svg>
        `;
    }
    renderLogoSlot(slotIndex) {
        return html `
            <li class="item">
                <div class="logo-container">
                    <div class="logo-wrap">
                        ${BRAND_LOGOS.map((logo, logoIndex) => this.renderLogoLayer(logo, logoIndex, slotIndex))}
                    </div>
                </div>
            </li>
        `;
    }
    render() {
        const ariaLabel = BRAND_LOGOS.map((logo) => logo.name).join(", ");
        const slots = Array.from({ length: Math.min(DESKTOP_SLOT_COUNT, BRAND_LOGOS.length) }, (_, slotIndex) => this.renderLogoSlot(slotIndex));
        return html `
            <div class="rotating-brand-line">
                ${this.label ? html `<p>${this.label}</p>` : nothing}
                <ul class="content" role="group" aria-label=${ariaLabel}>
                    ${slots}
                </ul>
            </div>
        `;
    }
    static { this.styles = css `
        * {
            box-sizing: border-box;
        }

        :host {
            display: block;
            inline-size: 100%;
        }

        .rotating-brand-line {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            box-shadow: inset 0 -1px 0 rgba(0, 0, 0, .04);
            padding-block-end: 40px;
        }

        p {
            margin: 0;
            color: var(--color-text-muted);
            font-weight: 500;
        }

        .content {
            display: grid;
            list-style-type: none;
            padding: 0;
            margin: 0;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 4px;
            inline-size: 100%;
        }

        @media (min-width: 961px) {
            .content {
                grid-template-columns: repeat(5, minmax(0, 1fr));
            }
        }

        .item {
            position: relative;
        }

        .item:nth-child(n + 5) {
            display: none;
        }

        @media (min-width: 961px) {
            .item:nth-child(n + 5) {
                display: block;
            }
        }

        .logo-container {
            position: relative;
            z-index: 1;
            display: flex;
            align-items: center;
            align-self: stretch;
            justify-content: center;
            inline-size: 100%;
            max-inline-size: 100%;
            container-type: inline-size;
            overflow: hidden;
            aspect-ratio: 267 / 144;
        }

        .logo-wrap {
            --rotating-brand-logo-padding-inline: 14px;

            position: relative;
            display: grid;
            place-items: center;
            inline-size: 100%;
            block-size: 100%;
        }

        @media (min-width: 641px) {
            .logo-wrap {
                --rotating-brand-logo-padding-inline: 24px;
            }
        }

        .logo-layer {
            position: absolute;
            inset: 0;
            display: block;
            margin: auto;
            inline-size: min(
                calc(100% - (var(--rotating-brand-logo-padding-inline) * 2)),
                var(--rotating-brand-logo-max-inline-size, 118px)
            );
            block-size: auto;
            max-inline-size: calc(100% - (var(--rotating-brand-logo-padding-inline) * 2));
            max-block-size: var(--rotating-brand-logo-max-block-size, 28px);
            opacity: 0;
            transform: translateY(24px);
            object-fit: contain;
            transition:
                opacity ${TRANSITION_MS}ms cubic-bezier(0.25, 1, 0.5, 1),
                transform ${TRANSITION_MS}ms cubic-bezier(0.25, 1, 0.5, 1);
            transition-delay: var(--rotating-brand-logo-delay, 0ms);
            will-change: opacity, transform;
        }

        .logo-layer[data-logo-state="current"] {
            opacity: 1;
            transform: translateY(0);
        }

        .logo-layer[data-logo-state="outgoing"] {
            opacity: 0;
            transform: translateY(-24px);
        }

        @media (prefers-reduced-motion: reduce) {
            .logo-layer {
                transition: none;
                will-change: auto;
            }
        }
    `; }
};
__decorate([
    property()
], RotatingBrandLineElement.prototype, "label", void 0);
__decorate([
    state()
], RotatingBrandLineElement.prototype, "activeLogoOffset", void 0);
__decorate([
    state()
], RotatingBrandLineElement.prototype, "previousLogoOffset", void 0);
RotatingBrandLineElement = __decorate([
    customElement(ROTATING_BRAND_LINE_TAG)
], RotatingBrandLineElement);
export { RotatingBrandLineElement };
